import { useMemo, useState } from "react";
import {
	RejectionConsequences,
	RejectionReasonOption,
} from "@/domain/eventInstance.interface";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";

/**
 * SIMS-8482 — a rejection carries a coded reason and, where the reason demands
 * it, a written note.
 *
 * The reasons arrive on the review detail rather than being listed here: they are
 * per event (SIMS-8490 widens that), and each row has to show BOTH halves — the
 * internal description that helps a coordinator pick correctly, and the exact
 * sentence the participant will receive. Showing both is what makes the mapping
 * from internal classification to participant message legible without opening the
 * notification templates.
 */
interface Props {
	open: boolean;
	eventName?: string;
	loading?: boolean;
	reasons: RejectionReasonOption[];
	consequences?: RejectionConsequences;
	onClose: () => void;
	onConfirm: (rejectionReasonId: string, internalNote?: string) => void;
}

const MAX_NOTE = 1000;

const formatAmount = (amount: number): string =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "USD",
	}).format(amount);

export const RejectDialog = ({
	open,
	eventName,
	loading,
	reasons,
	consequences,
	onClose,
	onConfirm,
}: Props) => {
	const [reasonId, setReasonId] = useState<string>("");
	const [note, setNote] = useState("");

	const selected = useMemo(
		() => reasons.find((r) => r.id === reasonId),
		[reasons, reasonId],
	);

	// AC2 + AC5. The confirm action is unavailable until a reason is chosen, and
	// stays unavailable while a reason that requires a note has none. Required-ness
	// is a property of the SELECTED REASON, so it changes with the selection.
	const blocked =
		!selected ||
		(selected.requiresNote && note.trim().length === 0) ||
		note.trim().length > MAX_NOTE;

	// AC9. Where the plan allows no further attempt, the confirm action itself says
	// so — the decision closes the event, and the button is the last place that can
	// be said before it happens.
	//
	// 🔴 `=== false`, never a truthiness test: `furtherAttemptAllowed` is tri-state
	// and `null` means unresolved. Only a definite "no" may relabel the button.
	const closesEvent = consequences?.furtherAttemptAllowed === false;

	const reset = (): void => {
		setReasonId("");
		setNote("");
	};

	const handleClose = (): void => {
		reset();
		onClose();
	};

	const handleConfirm = (): void => {
		if (blocked || !selected) return;
		onConfirm(selected.id, note.trim() || undefined);
		reset();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Reject submission</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ mb: 2 }}>
					Why are you rejecting this{" "}
					<strong>{eventName ?? "submission"}</strong>? Rejecting the submission
					rejects all components.
				</DialogContentText>

				{/* AC9 — what this costs the participant and what they may still do,
				    both taken from the plan's configuration for this event. */}
				{consequences && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						{consequences.incentiveAtStake != null && (
							<Typography variant="body2">
								This submission would have earned{" "}
								<strong>{formatAmount(consequences.incentiveAtStake)}</strong>.
							</Typography>
						)}
						{/* Three states, three sentences. `null` is not a "no" — it
						    means the plan's workflow could not be resolved, and saying
						    "cannot submit again" there states a closure nobody
						    established. The honest version names the uncertainty and
						    points at the cause, because a missing workflow is a
						    configuration problem someone can go and fix. */}
						<Typography variant="body2">
							{consequences.furtherAttemptAllowed === true &&
								"The participant may submit this activity again."}
							{consequences.furtherAttemptAllowed === false &&
								"The participant cannot submit this activity again — rejecting closes the event."}
							{consequences.furtherAttemptAllowed == null &&
								"Whether the participant may submit this activity again could not be determined — this event has no matching workflow step in the treatment plan."}
						</Typography>
					</Alert>
				)}

				<RadioGroup
					value={reasonId}
					onChange={(e) => setReasonId(e.target.value)}
					sx={{ mb: 2 }}
				>
					{reasons.map((reason) => (
						<FormControlLabel
							key={reason.id}
							value={reason.id}
							control={<Radio size="small" sx={{ alignSelf: "flex-start" }} />}
							sx={{ alignItems: "flex-start", mb: 1.5 }}
							label={
								<>
									<Typography variant="subtitle2">
										{reason.title}
										{reason.requiresNote && " · note required"}
									</Typography>
									{/* Both halves on the row — AC3. */}
									<Typography variant="body2" color="text.secondary">
										{reason.description}
									</Typography>
									<Typography
										variant="body2"
										sx={{ fontStyle: "italic", mt: 0.5 }}
									>
										The participant will be told: “{reason.participantCopy}”
									</Typography>
								</>
							}
						/>
					))}
				</RadioGroup>

				{/* AC4 — the note is internal. The label says so, because a coordinator
				    deciding what to write needs to know who can read it. */}
				<TextField
					label={
						selected?.requiresNote
							? "Internal note (required)"
							: "Internal note (optional)"
					}
					required={Boolean(selected?.requiresNote)}
					fullWidth
					multiline
					rows={3}
					value={note}
					onChange={(e) => setNote(e.target.value)}
					inputProps={{ maxLength: MAX_NOTE }}
					helperText={
						selected?.requiresNote && note.trim().length === 0
							? "This reason requires a note. It is never shown to the participant."
							: "Only staff can see this note. It is never shown to the participant."
					}
					error={Boolean(selected?.requiresNote && note.trim().length === 0)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="error"
					onClick={handleConfirm}
					disabled={loading || blocked}
				>
					{closesEvent ? "Reject and close event" : "Confirm Rejection"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
